import React, { useState } from 'react';
import { db } from '../firebaseConfig';
import { doc, setDoc } from 'firebase/firestore';

const QUESTION_TYPES = [
  { value: 'yes_no',         label: 'Yes / No' },
  { value: 'scale',          label: 'Scale (1–100)' },
  { value: 'multi_mcq',      label: 'Multiple Choice (multi)' },
  { value: 'single_mcq',     label: 'Multiple Choice (single)' },
  { value: 'paragraph',      label: 'Short Paragraph' },
  { value: 'photo',          label: 'Photo Upload' },
  { value: 'emoji_selector', label: 'Emoji Selector' },
];

export default function AdminDashboard({ questions, setQuestions, onBack, onLaunched }) {
  const [prompt, setPrompt]           = useState('');
  const [type, setType]               = useState('yes_no');
  const [options, setOptions]         = useState('');
  const [launching, setLaunching]     = useState(false);
  const [generatedPin, setGeneratedPin] = useState(null);

  const exportQuestions = () => {
    const data = JSON.stringify(questions, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'questions.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const importQuestions = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        if (!Array.isArray(parsed)) throw new Error('Expected an array of questions.');
        // Re-stamp IDs to avoid collisions with existing questions
        const imported = parsed.map(q => ({ ...q, id: Date.now() + Math.random() }));
        setQuestions(prev => [...prev, ...imported]);
      } catch (err) {
        alert('Invalid file: ' + err.message);
      }
    };
    reader.readAsText(file);
    // Reset input so the same file can be re-imported if needed
    e.target.value = '';
  };

  const addQuestion = () => {
    const text = prompt.trim();
    if (!text) return;
    setQuestions([...questions, {
      id: Date.now(),
      type,
      prompt: text,
      options: (type === 'single_mcq' || type === 'multi_mcq')
        ? options.split(',').map(o => o.trim()).filter(Boolean)
        : [],
    }]);
    setPrompt('');
    setOptions('');
  };

  const removeQuestion = (id) => setQuestions(questions.filter(q => q.id !== id));

  const launchGame = async () => {
    if (questions.length === 0) return alert('Add at least one question before launching.');
    setLaunching(true);
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const pin = Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    try {
      await setDoc(doc(db, 'games', pin), {
        pin,
        status: 'lobby',
        paxCount: 0,
        currentQuestionIndex: -1,
        questions,
        responses: {},
      });
      setGeneratedPin(pin);
    } catch (e) {
      alert('Failed to create game: ' + e.message);
    }
    setLaunching(false);
  };

  if (generatedPin) {
    const hostUrl   = `${window.location.origin}${window.location.pathname}?mode=host&pin=${generatedPin}`;
    const playerUrl = `${window.location.origin}${window.location.pathname}?mode=player`;
    return (
      <div className="page-center">
        <div className="container" style={{ maxWidth: 520 }}>
          <div className="card card--hero text-center" style={{ marginBottom: 20 }}>
            <div className="pin-label">Game Ready!</div>
            <div className="pin-display">{generatedPin}</div>
            <div className="pin-label" style={{ marginTop: 8 }}>Share this PIN with players</div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="label">Host Link (your screen)</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
              <input className="input" readOnly value={hostUrl} onFocus={e => e.target.select()} style={{ fontSize: '0.8rem' }} />
              <button className="btn btn--ghost btn--sm" onClick={() => navigator.clipboard.writeText(hostUrl)}>Copy</button>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 24 }}>
            <div className="label">Player Link (share with participants)</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
              <input className="input" readOnly value={playerUrl} onFocus={e => e.target.select()} style={{ fontSize: '0.8rem' }} />
              <button className="btn btn--ghost btn--sm" onClick={() => navigator.clipboard.writeText(playerUrl)}>Copy</button>
            </div>
            <p className="muted" style={{ marginTop: 8, fontSize: '0.8rem' }}>Players enter PIN <strong>{generatedPin}</strong> after opening the link.</p>
          </div>

          <button className="btn btn--primary btn--full btn--lg" onClick={() => onLaunched(generatedPin)}>
            Open Host Screen →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-center" style={{ alignItems: 'flex-start', paddingTop: 32 }}>
      <div className="container">
        <div className="flex items-center justify-between" style={{ marginBottom: 24 }}>
          <button className="btn btn--ghost btn--sm" onClick={onBack}>← Back</button>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 800 }}>Question Dashboard</h1>
          <div style={{ width: 80 }} />
        </div>

        {/* Add question form */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="section-title">Add a Question</div>

          <div style={{ marginBottom: 14 }}>
            <label className="label">Question Type</label>
            <select className="input" value={type} onChange={e => setType(e.target.value)}>
              {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label className="label">Prompt</label>
            <input
              className="input input--lg"
              type="text"
              placeholder="Type your question here..."
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addQuestion()}
            />
          </div>

          {(type === 'single_mcq' || type === 'multi_mcq') && (
            <div style={{ marginBottom: 14 }}>
              <label className="label">Options (comma-separated)</label>
              <input
                className="input"
                type="text"
                placeholder="Option A, Option B, Option C"
                value={options}
                onChange={e => setOptions(e.target.value)}
              />
            </div>
          )}

          <button className="btn btn--primary" onClick={addQuestion} disabled={!prompt.trim()}>
            + Add Question
          </button>
        </div>

        {/* Question list */}
        <div style={{ marginBottom: 24 }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
            <div className="section-title" style={{ marginBottom: 0 }}>
              Questions <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({questions.length})</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <label className="btn btn--ghost btn--sm" style={{ cursor: 'pointer', marginBottom: 0 }}>
                ↑ Import
                <input type="file" accept=".json" onChange={importQuestions} style={{ display: 'none' }} />
              </label>
              {questions.length > 0 && (
                <button className="btn btn--ghost btn--sm" onClick={exportQuestions}>
                  ↓ Export
                </button>
              )}
            </div>
          </div>

          {questions.length === 0 && (
            <div className="card text-center" style={{ padding: '32px 20px' }}>
              <p className="muted">No questions yet. Add one above.</p>
            </div>
          )}

          {questions.map((q, idx) => (
            <div key={q.id} className="question-item">
              <div className="question-item__num">{idx + 1}</div>
              <div style={{ flex: 1 }}>
                <div className="question-item__type">{QUESTION_TYPES.find(t => t.value === q.type)?.label}</div>
                <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{q.prompt}</div>
                {q.options?.length > 0 && (
                  <div className="muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>
                    {q.options.join(' · ')}
                  </div>
                )}
              </div>
              <button
                className="btn btn--sm"
                style={{ background: 'transparent', color: 'var(--danger)', border: 'none', padding: '4px 8px' }}
                onClick={() => removeQuestion(q.id)}
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        {questions.length > 0 && (
          <button
            className="btn btn--success btn--full btn--lg"
            onClick={launchGame}
            disabled={launching}
          >
            {launching ? 'Creating Game...' : 'Deploy & Generate Access Code'}
          </button>
        )}
      </div>
    </div>
  );
}
